import { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Building2, CheckCircle, FileText, BarChart, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HotelPartnersPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const benefits = [
    {
      icon: BarChart,
      title: "Community Impact Reporting",
      description: "Receive detailed reports on your social impact contribution for franchising and corporate reporting needs."
    },
    {
      icon: FileText,
      title: "Tax Benefits",
      description: "Donated room nights may qualify for tax deductions as charitable contributions."
    },
    {
      icon: Building2,
      title: "Brand Recognition",
      description: "Be recognized as a compassionate community partner committed to helping families in need."
    }
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/partners/hotels`, {
        hotel_name: data.hotelName,
        contact_name: data.contactName,
        email: data.email,
        phone: data.phone,
        city: data.city,
        state: data.state,
        rooms_available: data.roomsAvailable ? parseInt(data.roomsAvailable) : null,
        message: data.message
      });
      
      toast.success("Thank you! Your inquiry has been submitted successfully.");
      setSubmitted(true);
      reset();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1769123300291-81262063e667?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHx3YXJtJTIwY296eSUyMGhvdGVsJTIwcm9vbSUyMGludGVyaW9yJTIwc3VubGlnaHR8ZW58MHx8fHwxNzcwMzQ3NTY0fDA&ixlib=rb-4.1.0&q=85')`,
            filter: 'saturate(0.45) brightness(0.9)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl space-y-6 animate-fade-in-up">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Hotel Partners</span>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
              Turn Empty Rooms Into <span className="text-accent">Acts of Hope</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed">
              Partner with Hope Hospitality to provide unused rooms to families visiting 
              loved ones in the hospital. Make a difference while gaining valuable 
              community impact documentation.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 lg:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Partnership Benefits</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              Why Partner With Us
            </h2>
            <p className="text-muted-foreground mt-4">
              Joining Hope Hospitality as a hotel partner offers meaningful benefits 
              for your business and your community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className="bg-card border-border/50 card-hover"
                data-testid={`benefit-card-${index}`}
              >
                <CardContent className="p-8 space-y-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                    <benefit.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-foreground">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">The Process</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Reach Out", desc: "Fill out our partnership form below" },
              { step: "2", title: "Connect", desc: "Our team will contact you to discuss details" },
              { step: "3", title: "Partner", desc: "We'll set up the partnership agreement" },
              { step: "4", title: "Impact", desc: "Start helping families and receive impact reports" }
            ].map((item, index) => (
              <div key={index} className="text-center relative" data-testid={`process-step-${index}`}>
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground font-serif text-2xl font-bold flex items-center justify-center mx-auto">
                  {item.step}
                </div>
                {index < 3 && (
                  <ArrowRight className="hidden md:block absolute top-8 right-0 transform translate-x-1/2 w-6 h-6 text-muted-foreground" />
                )}
                <h3 className="font-serif text-lg font-bold text-foreground mt-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm mt-2">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 lg:py-32 bg-secondary" id="contact-form">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {submitted ? (
            <Card className="bg-card border-border/50" data-testid="submission-success">
              <CardContent className="p-12 text-center space-y-4">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-primary" />
                </div>
                <h2 className="font-serif text-2xl font-bold text-foreground">
                  Thank You!
                </h2>
                <p className="text-muted-foreground">
                  Your inquiry has been received. Our team will contact you within 2-3 business days 
                  to discuss partnership opportunities.
                </p>
                <Button 
                  onClick={() => setSubmitted(false)}
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground mt-4"
                >
                  Submit Another Inquiry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border/50">
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-serif text-2xl">Partner With Us</CardTitle>
                <CardDescription>
                  Fill out the form below and our team will reach out to discuss partnership opportunities.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="hotel-partner-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="hotelName">Hotel Name *</Label>
                      <Input 
                        id="hotelName"
                        {...register("hotelName", { required: "Hotel name is required" })}
                        placeholder="Comfort Inn Downtown"
                        className="input-focus"
                        data-testid="hotel-name-input"
                      />
                      {errors.hotelName && (
                        <p className="text-destructive text-sm">{errors.hotelName.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <Input 
                        id="contactName"
                        {...register("contactName", { required: "Contact name is required" })}
                        placeholder="John Smith"
                        className="input-focus"
                        data-testid="contact-name-input"
                      />
                      {errors.contactName && (
                        <p className="text-destructive text-sm">{errors.contactName.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        {...register("email", { 
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                        placeholder="john@hotel.com"
                        className="input-focus"
                        data-testid="email-input"
                      />
                      {errors.email && (
                        <p className="text-destructive text-sm">{errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input 
                        id="phone"
                        {...register("phone", { required: "Phone is required" })}
                        placeholder="(555) 123-4567"
                        className="input-focus"
                        data-testid="phone-input"
                      />
                      {errors.phone && (
                        <p className="text-destructive text-sm">{errors.phone.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input 
                        id="city"
                        {...register("city", { required: "City is required" })}
                        placeholder="Anytown"
                        className="input-focus"
                        data-testid="city-input"
                      />
                      {errors.city && (
                        <p className="text-destructive text-sm">{errors.city.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Input 
                        id="state"
                        {...register("state", { required: "State is required" })}
                        placeholder="CA"
                        className="input-focus"
                        data-testid="state-input"
                      />
                      {errors.state && (
                        <p className="text-destructive text-sm">{errors.state.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="roomsAvailable">Estimated Rooms Available Per Month (Optional)</Label>
                    <Input 
                      id="roomsAvailable"
                      type="number"
                      {...register("roomsAvailable")}
                      placeholder="10"
                      className="input-focus"
                      data-testid="rooms-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Additional Message (Optional)</Label>
                    <Textarea 
                      id="message"
                      {...register("message")}
                      placeholder="Tell us about your hotel and interest in partnering..."
                      rows={4}
                      className="input-focus resize-none"
                      data-testid="message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 btn-hover"
                    data-testid="submit-hotel-form"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Partnership Inquiry"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}
